import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { 
  PlusIcon,
  EyeIcon,
  CalendarIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { ClockIcon } from '@heroicons/react/24/solid';

interface LeaveRequest {
  id: number;
  userId: string;
  leaveType: {
    id: number;
    name: string;
    code: string;
  };
  startDate: string;
  endDate: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: string;
  workingDays?: number;
}

export const MyLeaveRequests = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAdmin, canApproveLeaves } = usePermissions();
  const { t } = useTranslation();

  // Fetch my leave requests
  const { data: leaveRequests, isLoading } = useQuery({
    queryKey: ['myLeaveRequests'],
    queryFn: async () => {
      const response = await api.get('/leaves/my');
      console.log('My leave requests:', response.data);
      const requests = response.data.leaveRequests || response.data.leaves || response.data.data || response.data || [];
      
      return requests.map((request: any) => ({
        id: request.id,
        userId: request.user_id || request.userId,
        startDate: request.start_date || request.startDate,
        endDate: request.end_date || request.endDate,
        reason: request.reason,
        status: request.status,
        createdAt: request.created_at || request.createdAt,
        leaveType: request.leaveType || request.leave_type,
        workingDays: calculateWorkingDays(request.start_date || request.startDate, request.end_date || request.endDate)
      }));
    }
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const response = await api.post(`/leaves/${requestId}/cancel`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
    }
  });

  // Approve mutation (for admin self-approval)
  const approveMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const response = await api.post(`/leaves/${requestId}/approve`, {
        comment: 'Admin self-approval'
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
    }
  });

  // Reject mutation (for admin self-rejection)
  const rejectMutation = useMutation({
    mutationFn: async (requestId: number) => {
      const response = await api.post(`/leaves/${requestId}/reject`, {
        comment: 'Admin self-rejection'
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] });
    }
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = parseISO(dateString);
      return format(date, 'dd.MM.yyyy', { locale: pl });
    } catch (error) {
      console.error('Date formatting error:', error);
      return '-';
    }
  };

  const getStatusBadgeVariant = (status: string): 'warning' | 'success' | 'error' | 'default' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Oczekujący';
      case 'approved':
        return 'Zatwierdzony';
      case 'rejected':
        return 'Odrzucony';
      case 'cancelled':
        return 'Anulowany';
      default:
        return status;
    }
  };

  const handleCancel = (requestId: number) => {
    if (confirm('Czy na pewno chcesz anulować ten wniosek?')) {
      cancelMutation.mutate(requestId);
    }
  };

  const handleApprove = (requestId: number) => {
    if (confirm('Czy na pewno chcesz zatwierdzić ten wniosek?')) {
      approveMutation.mutate(requestId);
    }
  };

  const handleReject = (requestId: number) => {
    if (confirm('Czy na pewno chcesz odrzucić ten wniosek?')) {
      rejectMutation.mutate(requestId);
    }
  };

  const calculateWorkingDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      let count = 0;
      const current = new Date(start);
      
      while (current <= end) {
        const dayOfWeek = current.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++;
        }
        current.setDate(current.getDate() + 1);
      }
      
      return count;
    } catch (error) {
      return 0;
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>;

  const pendingRequests = leaveRequests?.filter(req => req.status === 'pending') || [];
  const upcomingRequests = leaveRequests?.filter(req => 
    req.status === 'approved' && new Date(req.startDate) > new Date()
  ) || [];
  const pastRequests = leaveRequests?.filter(req => 
    req.status !== 'pending' && !(req.status === 'approved' && new Date(req.startDate) > new Date())
  ) || [];

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('myLeaveRequests.title')}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {t('myLeaveRequests.description', 'Przeglądaj i zarządzaj swoimi wnioskami urlopowymi')}
            </p>
          </div>
          <Button onClick={() => navigate('/leaves/new')}>
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('myLeaveRequests.newRequest')}
          </Button>
        </div>
      </div>

      {/* Pending Requests */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('myLeaveRequests.pendingRequests', 'Oczekujące wnioski')} ({pendingRequests.length})
        </h2>
        {pendingRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            {t('myLeaveRequests.noPendingRequests', 'Brak oczekujących wniosków')}
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {pendingRequests.map((request) => (
                <li key={request.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Badge variant="warning">
                            {getStatusText(request.status)}
                          </Badge>
                          <span className="ml-3 text-sm font-medium text-gray-900">
                            {request.leaveType?.name || 'Nieznany typ'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                            <span className="ml-2 font-medium">({request.workingDays} dni)</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {t('myLeaveRequests.submitted', 'Złożono')}: {formatDate(request.createdAt)}
                          </div>
                        </div>
                        {request.reason && (
                          <p className="mt-2 text-sm text-gray-600">
                            {t('myLeaveRequests.reason', 'Powód')}: {request.reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {isAdmin && canApproveLeaves && (
                          <>
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleApprove(request.id)}
                              disabled={approveMutation.isPending}
                            >
                              <CheckIcon className="h-4 w-4 mr-1" />
                              Zatwierdź
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleReject(request.id)}
                              disabled={rejectMutation.isPending}
                            >
                              <XMarkIcon className="h-4 w-4 mr-1" />
                              Odrzuć
                            </Button>
                          </>
                        )}
                        {!isAdmin && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleCancel(request.id)}
                            disabled={cancelMutation.isPending}
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            {t('myLeaveRequests.cancel')}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/leaves/${request.id}`)}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Upcoming Approved Requests */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('myLeaveRequests.upcomingLeaves', 'Nadchodzące urlopy')} ({upcomingRequests.length})
        </h2>
        {upcomingRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            {t('myLeaveRequests.noUpcomingLeaves', 'Brak zaplanowanych urlopów')}
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {upcomingRequests.map((request) => (
                <li key={request.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Badge variant="success">
                            {getStatusText(request.status)}
                          </Badge>
                          <span className="ml-3 text-sm font-medium text-gray-900">
                            {request.leaveType?.name || 'Nieznany typ'}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          <span className="ml-2 font-medium">({request.workingDays} dni)</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/leaves/${request.id}`)}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Past Requests */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Historia wniosków ({pastRequests.length})
        </h2>
        {pastRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            Brak historii wniosków
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {pastRequests.map((request) => (
                <li key={request.id}>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Badge variant={getStatusBadgeVariant(request.status)}>
                            {getStatusText(request.status)}
                          </Badge>
                          <span className="ml-3 text-sm font-medium text-gray-900">
                            {request.leaveType?.name || 'Nieznany typ'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <CalendarIcon className="h-4 w-4 mr-1" />
                            {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {formatDate(request.createdAt)}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/leaves/${request.id}`)}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};